import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FrameWithStickyTitle } from 'components/BrandSlides/BrandSlides';
import { ensureArray, safeMap, ensureString, ensureNumber } from 'utils/safeArray';

const Prioritization = ({ title, dots, legend, accent_color }) => {
  const chartRef = useRef(null);
  const safeDots = ensureArray(dots);
  const safeLegend = ensureArray(legend);

  useEffect(() => {
    const wrap = chartRef.current;
    if (!wrap) return;

    const dotElements = wrap.querySelectorAll('.dot');
    // Adjust padding for mobile
    const isMobile = window.innerWidth <= 768;
    const pad = isMobile
      ? { left: 40, right: 15, top: 15, bottom: 30 }
      : { left: 60, right: 20, top: 20, bottom: 40 };
    const W = wrap.clientWidth - pad.left - pad.right;
    const H = wrap.clientHeight - pad.top - pad.bottom;

    dotElements.forEach(d => {
      const x = (parseFloat(d.dataset.x) - 1) / 4; // 0..1
      const y = (parseFloat(d.dataset.y) - 1) / 4; // 0..1
      d.style.left = (pad.left + x * W - 5) + 'px';
      d.style.top = (pad.top + (1 - y) * H - 5) + 'px';
    });
  }, [safeDots]);

  return (
    <FrameWithStickyTitle title={ensureString(title)} accentColor={accent_color}>
      <div className="flex flex-1 flex-col gap-6">
        <div className="chart-wrap" aria-label="Value Feasibility Matrix" ref={chartRef}>
          <div className="quad"></div>

          {safeMap(safeDots, (dot, index) => {
            const safeDot = dot && typeof dot === 'object' ? dot : {};
            return (
              <div
                key={index}
                className={`dot ${safeDot.isRed ? 'red' : ''}`}
                data-x={ensureNumber(safeDot.x, 1)}
                data-y={ensureNumber(safeDot.y, 1)}
                title={ensureString(safeDot.title)}
              ></div>
            );
          })}

          <div className="axis-x"><span>1</span><span>2</span><span>3</span><span>4</span><span>5</span></div>
          <div className="axis-y"><span>5</span><span>4</span><span>3</span><span>2</span><span>1</span></div>

          <ul className="legend">
            {safeMap(safeLegend, (item, index) => {
              const safeItem = item && typeof item === 'object' ? item : {};
              return (
                <li key={index}>
                  <span className={`sw ${safeItem.isRed ? 'red' : ''}`}></span> {ensureString(safeItem.text)}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </FrameWithStickyTitle>
  );
};

Prioritization.propTypes = {
  title: PropTypes.string.isRequired,
  dots: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    isRed: PropTypes.bool,
  })).isRequired,
  legend: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string.isRequired,
    isRed: PropTypes.bool,
  })).isRequired,
  accent_color: PropTypes.string,
};

export default Prioritization;
